import { exhaustive } from "exhaustive"
import { FC, useEffect, useState } from "react"
import { useClient } from "../../Client/ClientAndUserProvider"
import { PenIcon } from "../../Icons/Icon"
import { Loader } from "../../Loader/Loader"
import { API } from "../../network/API"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import { AddEditConsumerTagModal, ConsumerTagModalMode } from "../AddEditConsumerTagModal/AddEditConsumerTagModal"
import style from "./CustomerPortalConsumerTags.module.css"

type GetConsumerTagBase = {
	id: string
	consumerRefs: string[]
}

export type GetConsumerGenericTag = GetConsumerTagBase & {
	type: "Generic"
	text: string
}

export type GetConsumerTag = GetConsumerGenericTag

export type GetConsumerTags = {
	tags: GetConsumerTag[]
}

type CustomerPortalConsumerTagsProps = {}

export const CustomerPortalConsumerTags: FC<CustomerPortalConsumerTagsProps> = (props: CustomerPortalConsumerTagsProps) => {
	const client = useClient()
	const permissions = usePermissions()

	const [tags, setTags] = useState<GetConsumerTag[]>([])
	const [editTag, setEditTag] = useState<"new" | GetConsumerTag | null>(null)

	const [dataLoading, setDataLoading] = useState(true)

	useEffect(() => {
		fetchAvailableTags()
	}, [])

	function fetchAvailableTags() {
		setDataLoading(true)
		setTags([])

		if (!permissions.isAllowed(PermissionAreaLocation.Consumer_Information_View)) {
			setDataLoading(false)
			return
		}

		API.getWithRetries<GetConsumerTags>(`/customer-portal/consumer-tags-v1/${client.identifier}`, true)
			.then((res) => {
				setTags(res.tags)
				setDataLoading(false)
			})
			.catch(() => {
				setTags([])
				setDataLoading(false)
			})
	}

	function addEditTagModal() {
		if (!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Information_Update) || !editTag) {
			return null
		}

		let mode: ConsumerTagModalMode

		if (editTag === "new") {
			mode = {
				type: "Add",
			}
		} else {
			mode = {
				type: "Edit",
				tag: editTag,
			}
		}

		return (
			<AddEditConsumerTagModal
				mode={mode}
				onClose={() => {
					setEditTag(null)
				}}
				onDone={() => {
					fetchAvailableTags()
					setEditTag(null)
				}}
			/>
		)
	}

	function tagsContent() {
		const allowedToEdit = permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Information_Update)

		return (
			<div className={style.contentWrapper}>
				<div className={style.header}>Kund-taggar</div>
				<div className={style.tagsWrapper}>
					{tags.map((tag) => {
						return exhaustive(tag, "type", {
							Generic: (it) => {
								return (
									<div
										key={it.id}
										className={style.consumerTag}
										onClick={() => {
											setEditTag(tag)
										}}>
										{it.text}

										{allowedToEdit ? <PenIcon size={16} iconClassName={style.editIcon} /> : null}
									</div>
								)
							},
						})
					})}
				</div>
				<div
					className={style.addTagButton}
					onClick={() => {
						setEditTag("new")
					}}>
					Lägg till
					<span style={{ fontSize: "24px" }}>+</span>
				</div>
			</div>
		)
	}

	if (!permissions.isAllowed(PermissionAreaLocation.Consumer_Information_View)) {
		return null
	}

	return (
		<div className={style.wrapper}>
			{dataLoading ? (
				<div style={{ display: "flex", justifyContent: "center" }}>
					<Loader />
				</div>
			) : (
				<>
					{addEditTagModal()}
					{tagsContent()}
				</>
			)}
		</div>
	)
}
